import React, { useState } from 'react'
import { useQuery, useLazyQuery } from '@apollo/client'
import { useAlert } from 'react-alert'
import get from 'lodash/get'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'
import { useStyles } from './styles'
import { useAuthorization } from '../../services/authorization'
import { GET_USER_PROFILE_BY_ID, CHANGE_PASSWORD } from '../../network/auth/queries'
import { PASS_REGEX, PASSWORD_EQUALITY_ERROR_TEXT, PASSWORD_COMPLEXITY_ERROR_TEXT } from '../../constants/auth'

const AccountSettingsPage = () => {
  const alert = useAlert()
  const [, , , , userId] = useAuthorization()
  const { loading: userDataLoading, data: userData } = useQuery(GET_USER_PROFILE_BY_ID, {
    variables: { userId }
  })
  const [changePassword, { loading }] = useLazyQuery(CHANGE_PASSWORD, {
    onError: error => {
      alert.show(error.message, {
        type: 'error'
      })
    },
    onCompleted: () => {
      setNewPasswordError('')
      setMatchPasswordsError('')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      alert.show('Password successfully changed.', {
        type: 'success'
      })
    }
  })
  const classes = useStyles()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [newPasswordError, setNewPasswordError] = useState('')
  const [matchPasswordsError, setMatchPasswordsError] = useState('')

  const handleCurrentPasswordChange = e => {
    setCurrentPassword(e.target.value)
  }

  const handleNewPasswordChange = e => {
    setNewPassword(e.target.value)
    setNewPasswordError('')
  }

  const handleConfirmPasswordChange = e => {
    setConfirmPassword(e.target.value)
    setMatchPasswordsError('')
  }

  const handleFormSubmit = e => {
    e.preventDefault()
    if (currentPassword && newPassword && confirmPassword) {
      if (newPassword.match(PASS_REGEX)) {
        if (newPassword === confirmPassword) {
          changePassword({
            variables: {
              currentPassword,
              newPassword
            }
          })
        } else {
          setMatchPasswordsError(PASSWORD_EQUALITY_ERROR_TEXT)
        }
      } else {
        setNewPasswordError(PASSWORD_COMPLEXITY_ERROR_TEXT)
      }
    }
  }

  return (
    <div className={classes.root}>
      <Grid container direction='column' justifyContent='center' alignItems='center' spacing={5}>
        <Grid container item direction='row' justifyContent='flex-start' alignItems='center'>
          <Grid item xs={12}>
            <Typography variant='h2' component='div'>
              Account Settings
            </Typography>
          </Grid>
        </Grid>
        <Grid container item direction='row' justifyContent='center' alignItems='center'>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Grid container direction='row'>
                <Grid item xs={3}>
                  <Typography variant='h4' component='div' className={classes.formHeader}>
                    Login Details
                  </Typography>
                </Grid>
                <Grid container item xs={9} direction='column' justifyContent='center' alignItems='center'>
                  <form onSubmit={handleFormSubmit} className={classes.form}>
                    <div className={classes.inputBlock}>
                      <div className={classes.emailRow}>
                        <Typography variant='h6' component='div' className={classes.label}>
                          EMAIL ADDRESS
                        </Typography>
                        <Typography variant='h5' component='div' className={classes.email}>
                          {userDataLoading ? 'Loading...' : get(userData, 'me.profile.email', null)}
                        </Typography>
                      </div>
                      <div className={classes.inputRow}>
                        {currentPassword ? (
                          <Typography variant='h6' component='label' className={classes.label} htmlFor='Current Password'>
                            CURRENT PASSWORD
                          </Typography>
                        ) : (
                          <div className={classes.labelPlaceHolder} />
                        )}
                        <TextField
                          classes={{
                            root: classes.input
                          }}
                          fullWidth
                          id='Current Password'
                          placeholder='Current Password'
                          variant='outlined'
                          name='Current Password'
                          value={currentPassword}
                          onChange={handleCurrentPasswordChange}
                          type='password'
                        />
                      </div>
                      <div className={classes.inputRow}>
                        {newPassword ? (
                          <Typography variant='h6' component='label' className={classes.label} htmlFor='New Password'>
                            NEW PASSWORD
                          </Typography>
                        ) : (
                          <div className={classes.labelPlaceHolder} />
                        )}
                        <TextField
                          classes={{
                            root: classes.input
                          }}
                          fullWidth
                          id='New Password'
                          placeholder='New Password'
                          variant='outlined'
                          name='New Password'
                          value={newPassword}
                          onChange={handleNewPasswordChange}
                          type='password'
                        />
                      </div>
                      {newPasswordError && (
                        <Typography variant='h6' component='span' className={classes.errorLabel}>
                          {newPasswordError}
                        </Typography>
                      )}
                      <div className={classes.inputRow}>
                        {confirmPassword ? (
                          <Typography variant='h6' component='label' className={classes.label} htmlFor='Confirm Password'>
                            CONFIRM PASSWORD
                          </Typography>
                        ) : (
                          <div className={classes.labelPlaceHolder} />
                        )}
                        <TextField
                          classes={{
                            root: classes.input
                          }}
                          fullWidth
                          id='Confirm Password'
                          placeholder='Confirm Password'
                          variant='outlined'
                          name='Confirm Password'
                          value={confirmPassword}
                          onChange={handleConfirmPasswordChange}
                          type='password'
                        />
                      </div>
                      {matchPasswordsError && (
                        <Typography variant='h6' component='span' className={classes.errorLabel}>
                          {matchPasswordsError}
                        </Typography>
                      )}
                    </div>
                    <div className={classes.buttonBlock}>
                      {loading ? (
                        <div className={classes.loader}>
                          <CircularProgress color='inherit' />
                        </div>
                      ) : (
                        <Button
                          disabled={!currentPassword || !newPassword || !confirmPassword}
                          className={classes.saveButton}
                          type='submit'
                        >
                          SAVE
                        </Button>
                      )}
                    </div>
                  </form>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

export default AccountSettingsPage
